import { ProLayout } from '@ant-design/pro-components'
import { Outlet, createFileRoute, redirect, useRouterState } from '@tanstack/react-router'
import { useUserStore } from '@tshared/stores/user.store'
import { Spin } from 'antd'
import { type MouseEvent, useEffect } from 'react'

import { organizationQueryOptions } from '@/apis/organization.api.ts'
import Logo from '@/components/brand/Logo.tsx'
import queryClient from '@/containers/query-client.ts'
import EmergencyCallButton from '@/routes/arc/-features/calling/components/call-out/EmergencyCallButton.tsx'
import { CallingWorkspace } from '@/routes/arc/-features/calling/components/calling-panel/CallingWorkspace.tsx'
import { UrgentCallNotification } from '@/routes/arc/-features/urgent-call-notification/UrgentCallNotification.tsx'

import '@/routes/arc/-styles/index.scss'
import { useOperatorStore } from '@/routes/arc/-stores/operator.store.ts'
import { BackgroundTask } from '@/routes/arc/-widgets/arc-background-task/BackgroundTask.tsx'
import Menu from '@/routes/arc/-widgets/arc-header/Menu.tsx'
import { OperatorInfo } from '@/routes/arc/-widgets/arc-header/OperatorInfo.tsx'
import OperatorStatusUpdateForm from '@/routes/arc/-widgets/operator-status-update-form/OperatorStatusUpdateForm.tsx'
import RejectCallReasonForm from '@/routes/arc/-widgets/reject-call-form/RejectCallReasonForm.tsx'
import SearchModal from '@/routes/arc/-widgets/search/SearchModal.tsx'
import { StatusesIndicator } from '@/routes/arc/-widgets/status-indicator/StatusesIndicator.tsx'
import { useHostInitialized } from '@/stores/host.store.tsx'
import { Host } from '@/widgets/hosts/Host.tsx'

export const Route = createFileRoute('/arc')({
  component: ArcLayout,
  beforeLoad: async () => {
    const user = useUserStore.getState().user
    if (!user?.accessToken) {
      return redirect({
        to: '/auth/sign-in',
        search: { redirect: location.pathname + location.search },
      })
    }
    return
  },
  loader: async () => {
    const organizationId = useUserStore.getState().user?.organization?.id
    if (!organizationId) return

    // An inbound call can ring immediately after entering the workspace, and its
    // countdown reads the org setting once on mount. Failures fall back to the default
    // rather than blocking access to the workspace.
    await queryClient.ensureQueryData(organizationQueryOptions(organizationId)).catch(() => {})
  },
})

function ArcLayout() {
  const initialized = useHostInitialized()
  const operatorStatus = useOperatorStore((state) => state.localStatus)
  const handleSkipToContent = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    window.location.hash = 'main-content'
    requestAnimationFrame(() => document.getElementById('main-content')?.focus())
  }

  return (
    <>
      <a className="skip-link" href="#main-content" onClick={handleSkipToContent}>
        Skip to main content
      </a>
      <ProLayout
        layout="top"
        fixedHeader
        menuRender={false}
        token={{
          header: {
            colorBgHeader: 'var(--header-background-color)',
            colorHeaderTitle: 'var(--header-title-color)',
            colorTextMenu: 'var(--header-menu-color)',
            colorTextMenuSecondary: 'var(--header-menu-color-secondary)',
            colorTextMenuSelected: 'var(--header-menu-color-selected)',
            colorBgMenuItemSelected: 'var(--header-menu-bg-selected)',
            colorTextMenuActive: 'var(--header-menu-color-active)',
            colorTextRightActionsItem: 'var(--header-actions-color)',
            heightLayoutHeader: 46,
          },
        }}
        headerTitleRender={() => <Logo size={32} />}
        headerContentRender={() => (
          <div className="flex items-center gap-4">
            <StatusesIndicator />
          </div>
        )}
        actionsRender={() => [
          <EmergencyCallButton key="emergency" operatorStatus={operatorStatus} />,
          operatorStatus !== 'OFFLINE' && <Menu key="menu" />,
          operatorStatus !== 'OFFLINE' && <SearchModal key="search" />,
          <OperatorInfo key="operator" />,
        ]}
        contentStyle={{
          padding: 0,
          height: 'calc(100vh - var(--header-height))',
          overflow: 'hidden',
        }}
      >
        {initialized ? (
          <section
            id="main-content"
            tabIndex={-1}
            style={{
              padding: 0,
              height: 'calc(100vh - var(--header-height))',
              overflow: 'hidden',
              display: 'flex',
            }}
          >
            <CurrentPageHeading />
            <Outlet />
          </section>
        ) : (
          <div className={'flex h-full items-center justify-center'}>
            <Spin size={'large'} />
          </div>
        )}
      </ProLayout>
      <OperatorStatusUpdateForm />
      <RejectCallReasonForm />
      <CallingWorkspace />
      <BackgroundTask />
      <Host />
      <UrgentCallNotification />
    </>
  )
}

const PAGE_TITLES: Array<[string, string]> = [
  ['/arc/status/admin', 'Call Logs'],
  ['/arc/status/available', 'Available calls'],
  ['/arc/status/offline', 'Offline status'],
  ['/arc/statistics', 'Statistics'],
  ['/arc/profile', 'Profile'],
  ['/arc/contact', 'Contact'],
]

function CurrentPageHeading() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const pageTitle = PAGE_TITLES.find(([path]) => pathname.startsWith(path))?.[1] ?? 'Workspace'

  useEffect(() => {
    document.title = `ILOS - ${pageTitle}`
  }, [pageTitle])

  return <h1 className="sr-only">{pageTitle}</h1>
}
